import type { TicketStatus } from './kds';
import type { FulfilmentStatus, OrderStatus, OrderType, PaymentStatus } from './orders';
import { isSettled } from './orders';

/**
 * Serving rules shared by the Expo board, the POS, the tests and the server (which re-validates
 * everything). Phase 6 (ADR-021): a production ticket is the unit of service, an order is
 * partially served until every live ticket has left the pass, completion needs paid and served,
 * and a dining table stays occupied until someone clears it.
 */

export const SERVE_DEFAULTS = {
  /** Owner decision 5: quiet undo inside the window, a reason and serve.correct after it. */
  undoWindowSeconds: 120,
  /** Owner decision 7: how long a ready order may wait at the pass. */
  warnSeconds: 120,
  lateSeconds: 300,
  /** The recently served drawer is bounded, like the KDS recall drawer. */
  recentLimit: 20,
  recentWindowMinutes: 120,
  /** Polling is the correctness path; realtime hints only shorten the wait. */
  expoPollSeconds: 15,
} as const;

/** The Expo board shows an order as soon as one station is ready (owner decision 2). */
export const EXPO_BOARD_STATUSES = [
  'partially_ready',
  'ready',
  'partially_served',
] as const satisfies readonly FulfilmentStatus[];

export type ServeCorrectionMode = 'undo' | 'correct' | 'approval';

/**
 * How a serve is taken back: `undo` inside the window (no reason), `correct` after it for an actor
 * holding serve.correct (reason required: the cashier or the owner at didto., ADR-022),
 * `approval` after it for anyone else (reason plus the PIN of a cashier or the owner on the same
 * terminal). A missing served time is treated as outside the window.
 */
export function serveCorrectionMode(
  servedAt: string | null,
  nowMs: number,
  windowSeconds: number,
  actorCanCorrect: boolean,
): ServeCorrectionMode {
  if (servedAt && nowMs - new Date(servedAt).getTime() <= windowSeconds * 1000) return 'undo';
  return actorCanCorrect ? 'correct' : 'approval';
}

/** Dine-in orders are served to the table; takeout and advance orders are picked up. */
export function readyForLabel(type: OrderType): 'READY FOR SERVING' | 'READY FOR PICKUP' {
  return type === 'dine_in' ? 'READY FOR SERVING' : 'READY FOR PICKUP';
}

export interface ServeTicketView {
  status: TicketStatus;
  ready_at: string | null;
}

export type ServeOffer = 'serve_all' | 'serve_ready' | 'none';

/**
 * Which serve action a card offers: `serve_all` when every live ticket is ready (or already
 * served) and at least one still waits, `serve_ready` when only some stations are ready (owner
 * decision 3: the ready portion may be served while another station is still preparing).
 */
export function serveOffer(tickets: readonly ServeTicketView[]): ServeOffer {
  const live = tickets.filter((t) => t.status !== 'cancelled');
  const ready = live.filter((t) => t.status === 'ready');
  if (ready.length === 0) return 'none';
  return live.every((t) => t.status === 'ready' || t.status === 'served')
    ? 'serve_all'
    : 'serve_ready';
}

/** Waiting time at the pass counts from the oldest ready ticket nobody has served yet. */
export function expoAgeAnchor(tickets: readonly ServeTicketView[]): string | null {
  let anchor: string | null = null;
  for (const t of tickets) {
    if (t.status !== 'ready' || !t.ready_at) continue;
    if (anchor === null || t.ready_at < anchor) anchor = t.ready_at;
  }
  return anchor;
}

/** Served but not settled: the order stays open on the POS with TO SETTLE (owner decision 4). */
export function isToSettle(o: {
  status: OrderStatus;
  fulfilment_status: FulfilmentStatus;
  payment_status: PaymentStatus;
}): boolean {
  return o.status === 'open' && o.fulfilment_status === 'served' && !isSettled(o.payment_status);
}

/** Completion rule (owner decision 4): settled (paid or written off), and either served or nothing to prepare. */
export function completesWhen(o: {
  payment_status: PaymentStatus;
  fulfilment_status: FulfilmentStatus;
}): boolean {
  return (
    isSettled(o.payment_status) &&
    (o.fulfilment_status === 'served' || o.fulfilment_status === 'not_required')
  );
}

export type TableStatus = 'available' | 'occupied';

/** Critical refinement 1: occupancy is a session, not an order state. */
export function tableStatus(
  session: { cleared_at: string | null } | null | undefined,
): TableStatus {
  return session && session.cleared_at === null ? 'occupied' : 'available';
}

export type TableClearMode = 'clear' | 'force' | 'blocked';

/**
 * ADR-022: a table with no open order is cleared with one tap (SERVER, CASHIER, OWNER); a table
 * whose orders are still open needs a force clear with a reason (CASHIER, OWNER); anyone else is
 * told to ask the cashier.
 */
export function tableClearMode(openOrders: number, canForce: boolean): TableClearMode {
  if (openOrders === 0) return 'clear';
  return canForce ? 'force' : 'blocked';
}

/** Critical refinement 3: a served line is never voided; unserved lines follow the existing rules. */
export function lineVoidBlockedBy(ticketStatus: TicketStatus | null | undefined): string | null {
  return ticketStatus === 'served' ? 'This line was already served; refund it instead.' : null;
}
